import { DEFAULT_ACCOUNT_POLICY, DEFAULT_CONFIG, DEFAULT_LIBRARY_DEFAULTS, DEFAULT_PLAN_LIMITS as COMPILED_PLAN_LIMITS, LIBRARY_DEFAULTS_CATALOG } from '@pockettavern/shared/config-defaults';
import { useCallback, useEffect, useState } from 'react';
import { ApiFailure, api, getBaseUrl, getLocale } from '../api';
import { useSession } from '../session';

interface Health {
  status: string;
  version: string;
  environment: string;
  services: Record<string, string>;
}
interface ConfigDoc {
  version: number;
  changed?: boolean;
  config?: Record<string, unknown>;
}

export function SystemPage() {
  const { env, isAdmin, me } = useSession();
  const [health, setHealth] = useState<Health | null>(null);
  const [healthError, setHealthError] = useState<string | null>(null);
  const [config, setConfig] = useState<ConfigDoc | null>(null);
  const [configSource, setConfigSource] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    setHealthError(null);
    try {
      const h = await api<Health>('/health', { auth: false });
      setHealth(h.data);
    } catch (err) {
      setHealth(null);
      setHealthError(err instanceof Error ? err.message : String(err));
    }
    try {
      // /api/config is served with Cache-Control max-age=60; bust the browser cache so invalidation shows immediately.
      const c = await api<ConfigDoc>(`/api/config?ts=${Date.now()}`, { auth: false });
      setConfig(c.data);
      setConfigSource(c.headers.get('X-Config-Source'));
    } catch (err) {
      setConfig(null);
      setMessage(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, env]);

  const invalidate = async () => {
    setMessage(null);
    try {
      const { data } = await api<{ version: number }>('/api/admin/config/invalidate', { method: 'POST' });
      setMessage(`已失效，新版本 ${data.version}`);
      await load();
    } catch (err) {
      setMessage(err instanceof ApiFailure ? `${err.error.code} — ${err.error.message}` : String(err));
    }
  };

  const badge = (s: string) => (
    <span className={`rounded px-2 py-0.5 text-xs font-mono ${s === 'ok' ? 'bg-emerald-900/60 text-emerald-200' : 'bg-amber-900/60 text-amber-200'}`}>{s}</span>
  );

  return (
    <div className="grid gap-6 md:grid-cols-2">
      <section className="card">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-semibold">健康检查</h2>
          <button className="btn-ghost" onClick={() => void load()}>刷新</button>
        </div>
        <p className="mb-3 text-xs text-zinc-400 font-mono">{getBaseUrl(env)}/health</p>
        {healthError && <p className="text-sm text-red-300">{healthError}</p>}
        {health && (
          <ul className="space-y-2 text-sm">
            <li className="flex justify-between"><span>gateway</span>{badge(health.status)}</li>
            {Object.entries(health.services).map(([name, s]) => (
              <li key={name} className="flex justify-between"><span>{name}</span>{badge(s)}</li>
            ))}
            <li className="flex justify-between text-zinc-400"><span>version / env</span><span className="font-mono">{health.version} / {health.environment}</span></li>
          </ul>
        )}
      </section>

      <section className="card">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-semibold">远程配置 <span className="text-xs text-zinc-400">版本 {config?.version ?? '—'}{configSource ? ` · 来源 ${configSource}` : ''}</span></h2>
          {me && (
            <button className="btn" disabled={!isAdmin} title={isAdmin ? '' : '需要 admin_role'} onClick={() => void invalidate()}>
              使失效（version+1）
            </button>
          )}
        </div>
        {message && <p className="mb-2 text-xs text-zinc-300">{message}</p>}
        <pre className="json">{JSON.stringify(config?.config ?? config, null, 2)}</pre>
      </section>

      <section className="card md:col-span-2">
        <h2 className="mb-3 font-semibold">运营配置（admin · finalize-platform-config）</h2>
        <p className="mb-2 text-xs text-zinc-500">PUT /api/admin/config/&#123;key&#125; 整体替换并失效缓存（约 1–2 分钟生效）。<span className="font-mono">official_defaults</span> 只能选启用中的模型；越界值 400，issues 显示在编辑框下方。models 仍走下方注册表。</p>
        <p className="mb-2 text-xs text-zinc-500">
          <span className="font-mono">ai_limits.max_concurrent_generations</span>（add-mvp-protocol-gaps-3）：可选键 <span className="font-mono">{'{ free: 1–20 | null, pro: 1–20 | null }'}</span>，缺省 / null = 不限用户级并发生成；设值后跨会话活动记录 ≥ 上限的新 compile / generate 返回 429 <span className="font-mono">CHAT_CONCURRENCY_LIMIT</span>（每会话锁 409 不变）。
        </p>
        <OpsConfigAdmin disabled={!isAdmin} config={config?.config ?? null} onChanged={load} />
      </section>

      <section className="card md:col-span-2">
        <h2 className="mb-3 font-semibold">新用户库默认 <span className="text-xs text-zinc-400">library_defaults（admin · add-library-defaults）</span></h2>
        <p className="mb-2 text-xs text-zinc-500">只影响<strong>尚未建库</strong>的账号（第一次访问私有库时写入人设 / 默认预设 / 全局世界书勾选）。已有 vault 的老用户（含从没打开过 Chat Settings 的）不会被改写。公开 <span className="font-mono">GET /api/config</span> 带此键，客户端不得拿它当当前用户的生效 Chat Settings。</p>
        <LibraryDefaultsAdmin disabled={!isAdmin} config={config?.config ?? null} onChanged={load} />
      </section>

      <section className="card md:col-span-2">
        <h2 className="mb-3 font-semibold">档位权益（admin · change 9）</h2>
        <p className="mb-2 text-xs text-zinc-500">PUT /api/admin/config/plan_limits · /tier_features 整体替换并失效缓存。「全员 pro 体验」= free 上限全部 null + free 加 custom_presets / local_backend；「恢复 v2 默认」= 编译期默认值（2026-09-09 起无上限、预设全员可编）。</p>
        <PlanAdmin disabled={!isAdmin} config={config?.config ?? null} onChanged={load} />
      </section>

      <section className="card md:col-span-2">
        <h2 className="mb-3 font-semibold">账号（admin · change 10）</h2>
        <p className="mb-2 text-xs text-zinc-500">PUT /api/admin/config/feature_flags。<span className="font-mono">mock_email</span> 开：非 production 的验证码 / 找回密码 / 邮箱验证 / 注销验证码响应带 <span className="font-mono">debug_code</span>（没有 RESEND_API_KEY 时自动 mock）。<span className="font-mono">mock_iap</span> 开（change 12）：非 production 的 billing 接受 JSON mock 收据与未签名 mock 商店通知（钻石页可模拟购买 / 续订 / 到期 / 退款）；production 忽略。<span className="font-mono">account_policy</span>（宽限天数 / 验证码参数）见上方配置 JSON；Apple / Google client id 是 auth 的 var，不在此配置。</p>
        <MockEmailToggle disabled={!isAdmin} config={config?.config ?? null} onChanged={load} />
      </section>

      <section className="card md:col-span-2">
        <h2 className="mb-3 font-semibold">钻石策略与客户端版本（admin · change 11）</h2>
        <p className="mb-2 text-xs text-zinc-500">PUT /api/admin/config/credits_policy · /client_latest_version · /billing_products 整体替换并失效缓存（约 1–2 分钟生效）。</p>
        <CreditsAdmin disabled={!isAdmin} config={config?.config ?? null} onChanged={load} />
      </section>

      <section className="card md:col-span-2">
        <h2 className="mb-3 font-semibold">平台标签词表 <span className="text-xs text-zinc-400">tag_taxonomy（admin · add-tag-taxonomy）</span></h2>
        <p className="mb-2 text-xs text-zinc-500">存 <span className="font-mono">app_config.tag_taxonomy</span>（全语言 <span className="font-mono">names</span>，BCP 47 key：<span className="font-mono">en</span> / <span className="font-mono">zh-CN</span> / <span className="font-mono">zh-TW</span> / <span className="font-mono">ja</span> / <span className="font-mono">ko</span>），公开 <span className="font-mono">GET /api/config</span> 不含该键；客户端经 <span className="font-mono">GET /api/tags/taxonomy</span> 按 <span className="font-mono">Accept-Language</span> 取单语言。颜色只挂分类；标签 <span className="font-mono">aliases</span> 只用于导入匹配。写入校验：key <span className="font-mono">^[a-z0-9_-]{'{1,64}'}$</span>、<span className="font-mono">tags[].category</span> 必须存在、<span className="font-mono">names</span> 含 <span className="font-mono">default_locale</span>、≤ 64 分类 / ≤ 1024 标签。</p>
        <TaxonomyAdmin disabled={!isAdmin} enabled={!!me} />
      </section>

      <section className="card md:col-span-2">
        <h2 className="mb-3 font-semibold">模型注册表（admin）</h2>
        <p className="mb-2 text-xs text-zinc-500">GET /api/models 对所有登录用户可见；PUT/DELETE /api/admin/models/&#123;id&#125; 需 admin_role。</p>
        <ModelsAdmin disabled={!isAdmin} />
      </section>
    </div>
  );
}

function issuesText(err: ApiFailure): string {
  const issues = err.error.issues as Array<{ path?: unknown[]; message?: string }> | undefined;
  if (!Array.isArray(issues) || issues.length === 0) return `${err.error.code} — ${err.error.message}`;
  return issues.map((i) => `${(i.path ?? []).join('.') || '?'}: ${i.message ?? err.error.message}`).join(' · ');
}

const OPS_DEFAULTS: Record<'client_min_version' | 'rate_limits' | 'ai_limits' | 'account_policy' | 'vault_quota_bytes' | 'media_limits', unknown> = {
  client_min_version: DEFAULT_CONFIG.client_min_version,
  rate_limits: DEFAULT_CONFIG.rate_limits,
  ai_limits: DEFAULT_CONFIG.ai_limits,
  account_policy: DEFAULT_ACCOUNT_POLICY,
  vault_quota_bytes: DEFAULT_CONFIG.vault_quota_bytes,
  media_limits: DEFAULT_CONFIG.media_limits,
};

function OpsConfigAdmin({ disabled, config, onChanged }: { disabled: boolean; config: Record<string, unknown> | null; onChanged: () => Promise<void> }) {
  const keys = ['client_min_version', 'rate_limits', 'ai_limits', 'account_policy', 'vault_quota_bytes', 'media_limits'] as const;
  const [texts, setTexts] = useState<Record<(typeof keys)[number], string>>(() => {
    const init = {} as Record<(typeof keys)[number], string>;
    for (const k of keys) init[k] = JSON.stringify(config?.[k] ?? OPS_DEFAULTS[k], null, 2);
    return init;
  });
  const [errors, setErrors] = useState<Partial<Record<(typeof keys)[number] | 'official_defaults', string>>>({});
  const [msg, setMsg] = useState<string | null>(null);
  const [models, setModels] = useState<Array<{ id: string; display_name: string; enabled?: boolean }>>([]);
  const [defaults, setDefaults] = useState<{ free: string; pro: string }>((config?.official_defaults as { free: string; pro: string } | undefined) ?? DEFAULT_CONFIG.official_defaults);
  useEffect(() => {
    const next = {} as Record<(typeof keys)[number], string>;
    for (const k of keys) next[k] = JSON.stringify(config?.[k] ?? OPS_DEFAULTS[k], null, 2);
    setTexts(next);
    if (config?.official_defaults) setDefaults(config.official_defaults as { free: string; pro: string });
  }, [config]);
  useEffect(() => {
    api<Array<{ id: string; display_name: string; enabled?: boolean }>>('/api/models?include_disabled=1')
      .then((r) => setModels(r.data.filter((m) => m.enabled !== false)))
      .catch(() => setModels([]));
  }, [config]);

  const put = async (key: (typeof keys)[number] | 'official_defaults', value: unknown) => {
    setMsg(null);
    setErrors((e) => ({ ...e, [key]: undefined }));
    try {
      const { data } = await api<{ version: number }>(`/api/admin/config/${key}`, { method: 'PUT', body: value });
      setMsg(`${key} 已写入，配置版本 ${data.version}（约 1–2 分钟生效）`);
      await onChanged();
    } catch (e) {
      const text = e instanceof ApiFailure ? issuesText(e) : String(e);
      setErrors((err) => ({ ...err, [key]: text }));
      setMsg(text);
    }
  };
  const parseOr = (text: string): unknown | undefined => {
    try { return JSON.parse(text) as unknown; } catch { setMsg('JSON 无法解析'); return undefined; }
  };

  return (
    <div className="space-y-3">
      {msg && <div className="text-xs text-zinc-300">{msg}</div>}
      <div className="grid gap-2 md:grid-cols-2">
        {keys.map((key) => (
          <div key={key}>
            <div className="mb-1 flex items-center justify-between text-xs text-zinc-400">
              <span className="font-mono">{key}</span>
              <span className="flex gap-1">
                <button className="btn-ghost py-0" disabled={disabled} onClick={() => setTexts((t) => ({ ...t, [key]: JSON.stringify(OPS_DEFAULTS[key], null, 2) }))}>恢复编译期默认</button>
                <button className="btn-ghost py-0" disabled={disabled} onClick={() => { const v = parseOr(texts[key]); if (v !== undefined) void put(key, v); }}>写入</button>
              </span>
            </div>
            <textarea className="input font-mono text-[11px]" rows={key === 'ai_limits' ? 16 : 8} value={texts[key]} onChange={(e) => setTexts((t) => ({ ...t, [key]: e.target.value }))} disabled={disabled} />
            {errors[key] && <p className="mt-1 text-[11px] text-red-300">{errors[key]}</p>}
          </div>
        ))}
        <div>
          <div className="mb-1 flex items-center justify-between text-xs text-zinc-400">
            <span className="font-mono">official_defaults</span>
            <button className="btn-ghost py-0" disabled={disabled} onClick={() => void put('official_defaults', defaults)}>写入</button>
          </div>
          <div className="grid gap-2 text-xs">
            {(['free', 'pro'] as const).map((tier) => (
              <label key={tier} className="flex items-center gap-2 text-zinc-400">
                {tier}
                <select className="input py-0.5 font-mono" value={defaults[tier]} disabled={disabled} onChange={(e) => setDefaults((d) => ({ ...d, [tier]: e.target.value }))}>
                  {models.map((m) => <option key={m.id} value={m.id}>{m.id} · {m.display_name}</option>)}
                </select>
              </label>
            ))}
          </div>
          {errors.official_defaults && <p className="mt-1 text-[11px] text-red-300">{errors.official_defaults}</p>}
        </div>
      </div>
    </div>
  );
}

type LibraryDefaultsForm = {
  persona: { name: string; description: string; avatar_color: string; avatar_text: string } | null;
  preset_name: string;
  global_select: string[];
};

function parseLibraryDefaults(raw: unknown): LibraryDefaultsForm {
  const d = DEFAULT_LIBRARY_DEFAULTS;
  const src = (raw && typeof raw === 'object' ? raw : d) as {
    persona?: { name?: string; description?: { description?: string; pockettavern?: { avatar_color?: string; avatar_text?: string } } } | null;
    preset_name?: string;
    global_select?: string[];
  };
  const pt = src.persona?.description?.pockettavern;
  return {
    persona: src.persona === null
      ? null
      : {
        name: src.persona?.name ?? d.persona?.name ?? 'Traveler',
        description: src.persona?.description?.description ?? '',
        avatar_color: pt?.avatar_color ?? '#c9a66b',
        avatar_text: pt?.avatar_text ?? 'T',
      },
    preset_name: src.preset_name ?? d.preset_name,
    global_select: Array.isArray(src.global_select) ? src.global_select : [],
  };
}

function toLibraryDefaultsPayload(form: LibraryDefaultsForm) {
  return {
    persona: form.persona === null
      ? null
      : {
        name: form.persona.name,
        description: {
          description: form.persona.description,
          pockettavern: { avatar_source: 'letter' as const, avatar_color: form.persona.avatar_color, avatar_text: form.persona.avatar_text },
        },
      },
    preset_name: form.preset_name,
    global_select: form.global_select,
  };
}

function LibraryDefaultsAdmin({ disabled, config, onChanged }: { disabled: boolean; config: Record<string, unknown> | null; onChanged: () => Promise<void> }) {
  const [form, setForm] = useState<LibraryDefaultsForm>(() => parseLibraryDefaults(config?.library_defaults));
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => {
    setForm(parseLibraryDefaults(config?.library_defaults));
  }, [config]);

  const put = async (value: unknown) => {
    setMsg(null);
    setError(null);
    try {
      const { data } = await api<{ version: number }>('/api/admin/config/library_defaults', { method: 'PUT', body: value });
      setMsg(`library_defaults 已写入，配置版本 ${data.version}（只影响尚未建库的账号；约 1–2 分钟生效）`);
      await onChanged();
    } catch (e) {
      const text = e instanceof ApiFailure ? issuesText(e) : String(e);
      setError(text);
      setMsg(text);
    }
  };

  return (
    <div className="space-y-3 text-xs">
      {msg && <div className="text-zinc-300">{msg}</div>}
      <label className="flex items-center gap-2 text-zinc-400">
        <input
          type="checkbox"
          checked={form.persona !== null}
          disabled={disabled}
          onChange={(e) => setForm((f) => ({
            ...f,
            persona: e.target.checked
              ? { name: 'Traveler', description: '', avatar_color: '#c9a66b', avatar_text: 'T' }
              : null,
          }))}
        />
        写入默认人设（关闭 = <span className="font-mono">persona: null</span>）
      </label>
      {form.persona && (
        <div className="grid gap-2 md:grid-cols-2">
          <label className="text-zinc-400">姓名
            <input className="input mt-1" disabled={disabled} value={form.persona.name} onChange={(e) => setForm((f) => ({ ...f, persona: f.persona ? { ...f.persona, name: e.target.value } : f.persona }))} />
          </label>
          <label className="text-zinc-400">描述
            <input className="input mt-1" disabled={disabled} value={form.persona.description} onChange={(e) => setForm((f) => ({ ...f, persona: f.persona ? { ...f.persona, description: e.target.value } : f.persona }))} />
          </label>
          <label className="text-zinc-400">letter 色
            <input className="input mt-1 font-mono" disabled={disabled} value={form.persona.avatar_color} onChange={(e) => setForm((f) => ({ ...f, persona: f.persona ? { ...f.persona, avatar_color: e.target.value } : f.persona }))} />
          </label>
          <label className="text-zinc-400">letter 字
            <input className="input mt-1" maxLength={2} disabled={disabled} value={form.persona.avatar_text} onChange={(e) => setForm((f) => ({ ...f, persona: f.persona ? { ...f.persona, avatar_text: e.target.value } : f.persona }))} />
          </label>
        </div>
      )}
      <label className="block text-zinc-400">默认预设
        <select className="input mt-1 font-mono" disabled={disabled} value={form.preset_name} onChange={(e) => setForm((f) => ({ ...f, preset_name: e.target.value }))}>
          {LIBRARY_DEFAULTS_CATALOG.openai_preset_names.map((n) => <option key={n} value={n}>{n}</option>)}
        </select>
      </label>
      <div className="text-zinc-400">种子世界书勾选
        <div className="mt-1 flex flex-wrap gap-3">
          {LIBRARY_DEFAULTS_CATALOG.world_book_names.map((name) => (
            <label key={name} className="flex items-center gap-1">
              <input
                type="checkbox"
                disabled={disabled}
                checked={form.global_select.includes(name)}
                onChange={(e) => setForm((f) => ({
                  ...f,
                  global_select: e.target.checked ? [...f.global_select, name] : f.global_select.filter((n) => n !== name),
                }))}
              />
              <span className="font-mono">{name}</span>
            </label>
          ))}
        </div>
      </div>
      <div className="flex gap-2">
        <button className="btn-ghost" disabled={disabled} onClick={() => setForm(parseLibraryDefaults(DEFAULT_LIBRARY_DEFAULTS))}>恢复编译期默认</button>
        <button className="btn" disabled={disabled} onClick={() => void put(toLibraryDefaultsPayload(form))}>写入</button>
      </div>
      {error && <p className="text-[11px] text-red-300">{error}</p>}
    </div>
  );
}

type TierLimit = { free: number | null; pro: number | null };
type PlanLimits = Record<'characters' | 'world_books' | 'personas' | 'ai_character_gen_per_day', TierLimit>;
type TierFeatures = { free: string[]; pro: string[] };
// v2 product defaults (2026-09-09): no count caps, AI character generation pro-only (free 0), preset editing for everyone
const DEFAULT_PLAN_LIMITS: PlanLimits = COMPILED_PLAN_LIMITS;
const DEFAULT_TIER_FEATURES: TierFeatures = DEFAULT_CONFIG.tier_features;
const LEGACY_PLAN_LIMITS: PlanLimits = { characters: { free: 10, pro: null }, world_books: { free: 5, pro: null }, personas: { free: 1, pro: null }, ai_character_gen_per_day: { free: 3, pro: null } };

function PlanAdmin({ disabled, config, onChanged }: { disabled: boolean; config: Record<string, unknown> | null; onChanged: () => Promise<void> }) {
  const limits = (config?.plan_limits as PlanLimits | undefined) ?? DEFAULT_PLAN_LIMITS;
  const features = (config?.tier_features as TierFeatures | undefined) ?? DEFAULT_TIER_FEATURES;
  const [limitsText, setLimitsText] = useState(JSON.stringify(limits, null, 2));
  const [featuresText, setFeaturesText] = useState(JSON.stringify(features, null, 2));
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => {
    setLimitsText(JSON.stringify(limits, null, 2));
    setFeaturesText(JSON.stringify(features, null, 2));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);

  const put = async (key: 'plan_limits' | 'tier_features', value: unknown) => {
    setMsg(null);
    try {
      const { data } = await api<{ version: number }>(`/api/admin/config/${key}`, { method: 'PUT', body: value });
      setMsg(`${key} 已写入，配置版本 ${data.version}`);
      await onChanged();
    } catch (e) {
      setMsg(e instanceof ApiFailure ? `${e.error.code} — ${e.error.message}` : String(e));
    }
  };
  const parseOr = (text: string): unknown | undefined => {
    try { return JSON.parse(text) as unknown; } catch { setMsg('JSON 无法解析'); return undefined; }
  };
  const openAll = async () => {
    const proFeatures = new Set([...features.pro, 'custom_presets', 'local_backend']);
    await put('plan_limits', { characters: { free: null, pro: null }, world_books: { free: null, pro: null }, personas: { free: null, pro: null }, ai_character_gen_per_day: { free: null, pro: null } });
    await put('tier_features', { free: [...new Set([...features.free, 'custom_presets', 'local_backend'])], pro: [...proFeatures] });
  };
  const restoreDefaults = async () => {
    await put('plan_limits', DEFAULT_PLAN_LIMITS);
    await put('tier_features', DEFAULT_TIER_FEATURES);
  };
  const legacyCaps = async () => {
    await put('plan_limits', LEGACY_PLAN_LIMITS);
    await put('tier_features', { free: features.free.filter((f) => f !== 'custom_presets'), pro: features.pro });
  };
  const freeOpen = limits.characters.free === null && features.free.includes('local_backend') && features.free.includes('custom_presets');

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <span className={`rounded px-2 py-0.5 font-mono ${freeOpen ? 'bg-emerald-900/60 text-emerald-200' : 'bg-zinc-800 text-zinc-300'}`}>{freeOpen ? '全员 pro 体验：开' : '按档位门禁：开'}</span>
        <button className="btn" disabled={disabled} onClick={() => void openAll()}>全员 pro 体验</button>
        <button className="btn-ghost" disabled={disabled} onClick={() => void restoreDefaults()}>恢复 v2 默认（无上限 · 预设全员可编）</button>
        <button className="btn-ghost" disabled={disabled} onClick={() => void legacyCaps()}>change 9 旧上限（10 / 5 / 1 / 3 · 预设只读）</button>
        {msg && <span className="text-zinc-300">{msg}</span>}
      </div>
      <div className="grid gap-2 md:grid-cols-2">
        <div>
          <div className="mb-1 flex items-center justify-between text-xs text-zinc-400"><span className="font-mono">plan_limits</span><button className="btn-ghost py-0" disabled={disabled} onClick={() => { const v = parseOr(limitsText); if (v !== undefined) void put('plan_limits', v); }}>写入</button></div>
          <textarea className="input font-mono text-[11px]" rows={8} value={limitsText} onChange={(e) => setLimitsText(e.target.value)} disabled={disabled} />
        </div>
        <div>
          <div className="mb-1 flex items-center justify-between text-xs text-zinc-400"><span className="font-mono">tier_features</span><button className="btn-ghost py-0" disabled={disabled} onClick={() => { const v = parseOr(featuresText); if (v !== undefined) void put('tier_features', v); }}>写入</button></div>
          <textarea className="input font-mono text-[11px]" rows={8} value={featuresText} onChange={(e) => setFeaturesText(e.target.value)} disabled={disabled} />
        </div>
      </div>
      <p className="text-[11px] text-zinc-500">features 写入后只影响下一次登录 / 刷新令牌时的 X-User-Features（gateway 按 tier_features 计算），已登录的会话需要重新登录才能看到新开关；plan_limits 在 KV 传播 + 60 s 边缘缓存后生效（约 1–2 分钟）。</p>
    </div>
  );
}

/** change 11: credits policy + in-app release notes, both admin-writable config keys. */
function CreditsAdmin({ disabled, config, onChanged }: { disabled: boolean; config: Record<string, unknown> | null; onChanged: () => Promise<void> }) {
  const [policyText, setPolicyText] = useState(JSON.stringify(config?.credits_policy ?? {}, null, 2));
  const [latestText, setLatestText] = useState(JSON.stringify(config?.client_latest_version ?? {}, null, 2));
  const [productsText, setProductsText] = useState(JSON.stringify(config?.billing_products ?? {}, null, 2));
  const [msg, setMsg] = useState<string | null>(null);
  useEffect(() => {
    setPolicyText(JSON.stringify(config?.credits_policy ?? {}, null, 2));
    setLatestText(JSON.stringify(config?.client_latest_version ?? {}, null, 2));
    setProductsText(JSON.stringify(config?.billing_products ?? {}, null, 2));
  }, [config]);
  const [memoryText, setMemoryText] = useState(JSON.stringify(config?.memory_policy ?? {}, null, 2));
  useEffect(() => { setMemoryText(JSON.stringify(config?.memory_policy ?? {}, null, 2)); }, [config]);
  const put = async (key: 'credits_policy' | 'client_latest_version' | 'billing_products' | 'memory_policy', text: string) => {
    setMsg(null);
    let value: unknown;
    try { value = JSON.parse(text) as unknown; } catch { setMsg('JSON 无法解析'); return; }
    try {
      const { data } = await api<{ version: number }>(`/api/admin/config/${key}`, { method: 'PUT', body: value });
      setMsg(`${key} 已写入，配置版本 ${data.version}`);
      await onChanged();
    } catch (e) {
      setMsg(e instanceof ApiFailure ? `${e.error.code} — ${e.error.message}` : String(e));
    }
  };
  return (
    <div className="space-y-2">
      {msg && <div className="text-xs text-zinc-300">{msg}</div>}
      <div className="grid gap-2 md:grid-cols-4">
        <div>
          <div className="mb-1 flex items-center justify-between text-xs text-zinc-400"><span className="font-mono">memory_policy</span><button className="btn-ghost py-0" disabled={disabled} onClick={() => void put('memory_policy', memoryText)}>写入</button></div>
          <textarea className="input font-mono text-[11px]" rows={14} value={memoryText} onChange={(e) => setMemoryText(e.target.value)} disabled={disabled} />
        </div>
        <div>
          <div className="mb-1 flex items-center justify-between text-xs text-zinc-400"><span className="font-mono">credits_policy</span><button className="btn-ghost py-0" disabled={disabled} onClick={() => void put('credits_policy', policyText)}>写入</button></div>
          <textarea className="input font-mono text-[11px]" rows={14} value={policyText} onChange={(e) => setPolicyText(e.target.value)} disabled={disabled} />
        </div>
        <div>
          <div className="mb-1 flex items-center justify-between text-xs text-zinc-400"><span className="font-mono">client_latest_version</span><button className="btn-ghost py-0" disabled={disabled} onClick={() => void put('client_latest_version', latestText)}>写入</button></div>
          <textarea className="input font-mono text-[11px]" rows={14} value={latestText} onChange={(e) => setLatestText(e.target.value)} disabled={disabled} />
        </div>
        <div>
          <div className="mb-1 flex items-center justify-between text-xs text-zinc-400"><span className="font-mono">billing_products</span><button className="btn-ghost py-0" disabled={disabled} onClick={() => void put('billing_products', productsText)}>写入</button></div>
          <textarea className="input font-mono text-[11px]" rows={14} value={productsText} onChange={(e) => setProductsText(e.target.value)} disabled={disabled} />
        </div>
      </div>
      <p className="text-[11px] text-zinc-500">charge_mode 默认 fixed：每轮扣 credits_per_turn（缺省 ceil(credits_per_1k)），与 token 数无关。charge_mode=token 才走旧公式 ceil(总 token × credits_per_1k / 1000)。credits_per_1k = ceil((in × (1 − blend_output_share) + out × blend_output_share) / 1000 × credits_per_usd × margin)，向上取整到整数、最低 1。daily_reward 按领取时档位；signup_bonus 默认 200；packs 是钻石包目录。client_latest_version 供强更页。billing_products：订阅 SKU、grace_days、clawback_credit_refunds。记忆（change 15）：memory_policy；全局熔断 feature_flags.memory_enabled，改 feature_flags 用上方 JSON。AI 造卡：feature ai_character_gen 在 tier_features.pro；ai_limits.character_gen 与超时在上方「运营配置」卡；日限 plan_limits.ai_character_gen_per_day.pro 默认 30。</p>
    </div>
  );
}

/** add-tag-taxonomy: raw (all-locale) read via the admin key endpoint, whole-document PUT. */
function TaxonomyAdmin({ disabled, enabled }: { disabled: boolean; enabled: boolean }) {
  interface Raw { default_locale: string; categories: Array<{ key: string; names: Record<string, string> }>; tags: Array<{ key: string; category: string; names: Record<string, string> }> }
  interface View { version: number; locale: string; categories: Array<{ key: string; name: string; color: string }>; tags: Array<{ key: string; category: string; name: string }> }
  const [text, setText] = useState('');
  const [version, setVersion] = useState<number | null>(null);
  const [view, setView] = useState<View | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const load = async () => {
    setMsg(null);
    try {
      const pub = await api<View>('/api/tags/taxonomy');
      setView(pub.data);
      if (!disabled) {
        const { data } = await api<{ version: number; value: Raw }>('/api/admin/config/tag_taxonomy');
        setVersion(data.version);
        setText(JSON.stringify(data.value, null, 2));
      }
    } catch (e) {
      setMsg(e instanceof ApiFailure ? `${e.error.code} — ${e.error.message}` : String(e));
    }
  };
  useEffect(() => { if (enabled) void load(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, disabled]);
  const put = async () => {
    setMsg(null);
    let value: unknown;
    try { value = JSON.parse(text) as unknown; } catch { setMsg('JSON 无法解析'); return; }
    try {
      const { data } = await api<{ version: number }>('/api/admin/config/tag_taxonomy', { method: 'PUT', body: value });
      setMsg(`tag_taxonomy 已写入，配置版本 ${data.version}（KV 传播 + 60 s 缓存后 /api/tags/taxonomy 生效）`);
      await load();
    } catch (e) {
      setMsg(e instanceof ApiFailure ? `${e.error.code} — ${e.error.message} ${JSON.stringify({ ...e.error, code: undefined, message: undefined }).slice(0, 600)}` : String(e));
    }
  };
  const locale = getLocale();
  return (
    <div className="space-y-2 text-xs">
      <div className="flex flex-wrap items-center gap-2">
        <button className="btn-ghost" onClick={() => void load()}>刷新</button>
        {view && <span className="text-zinc-400">公开视图：locale <span className="font-mono">{view.locale}</span> · v{view.version} · {view.categories.length} 分类 / {view.tags.length} 标签（console 请求头 Accept-Language 由顶栏语言决定，当前浏览器 {locale}）</span>}
        {version !== null && <span className="text-zinc-400">admin 原文版本 <span className="font-mono">{version}</span></span>}
        {msg && <span className="text-zinc-300">{msg}</span>}
      </div>
      {view && (
        <div className="flex flex-wrap gap-1">
          {view.categories.map((c) => (
            <span key={c.key} className="flex flex-wrap items-center gap-1 rounded-lg border border-zinc-800 px-1.5 py-0.5">
              <span className="text-[10px]" style={{ color: c.color }}>{c.name}</span>
              {view.tags.filter((t) => t.category === c.key).map((t) => <span key={t.key} className="rounded-full border px-2 py-0.5 text-[11px]" style={{ color: c.color, borderColor: c.color }} title={t.key}>{t.name}</span>)}
            </span>
          ))}
        </div>
      )}
      {!disabled && (
        <div>
          <div className="mb-1 flex items-center justify-between text-zinc-400"><span className="font-mono">GET / PUT /api/admin/config/tag_taxonomy</span><button className="btn-ghost py-0" onClick={() => void put()}>写入</button></div>
          <textarea className="input font-mono text-[11px]" rows={16} value={text} onChange={(e) => setText(e.target.value)} />
        </div>
      )}
    </div>
  );
}

function MockEmailToggle({ disabled, config, onChanged }: { disabled: boolean; config: Record<string, unknown> | null; onChanged: () => Promise<void> }) {
  const flags = (config?.feature_flags as Record<string, boolean> | undefined) ?? {};
  const policy = config?.account_policy as Record<string, number> | undefined;
  const [msg, setMsg] = useState<string | null>(null);
  const set = async (on: boolean, key: 'mock_email' | 'mock_iap' = 'mock_email') => {
    setMsg(null);
    try {
      const { data } = await api<{ version: number }>('/api/admin/config/feature_flags', { method: 'PUT', body: { ...flags, [key]: on } });
      setMsg(`feature_flags 已写入，配置版本 ${data.version}（约 1–2 分钟生效）`);
      await onChanged();
    } catch (e) {
      setMsg(e instanceof ApiFailure ? `${e.error.code} — ${e.error.message}` : String(e));
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span className={`rounded px-2 py-0.5 font-mono ${flags.mock_email ? 'bg-emerald-900/60 text-emerald-200' : 'bg-zinc-800 text-zinc-300'}`}>mock_email：{flags.mock_email ? '开' : '关'}</span>
      <button className="btn" disabled={disabled} onClick={() => void set(!flags.mock_email)}>{flags.mock_email ? '关闭 mock_email' : '开启 mock_email'}</button>
      <span className={`rounded px-2 py-0.5 font-mono ${flags.mock_iap ? 'bg-emerald-900/60 text-emerald-200' : 'bg-zinc-800 text-zinc-300'}`}>mock_iap：{flags.mock_iap ? '开' : '关'}</span>
      <button className="btn" disabled={disabled} onClick={() => void set(!flags.mock_iap, 'mock_iap')}>{flags.mock_iap ? '关闭 mock_iap' : '开启 mock_iap'}</button>
      {policy && <span className="font-mono text-zinc-400">account_policy: 宽限 {policy.deletion_grace_days} 天 · 码 {policy.otp_ttl_seconds}s / {policy.otp_max_attempts} 次 / 冷却 {policy.otp_resend_cooldown_seconds}s / 每小时 {policy.otp_hourly_limit}</span>}
      {msg && <span className="text-zinc-300">{msg}</span>}
    </div>
  );
}

function ModelsAdmin({ disabled }: { disabled: boolean }) {
  const [models, setModels] = useState<Array<Record<string, unknown>>>([]);
  const [id, setId] = useState('custom-model');
  const [json, setJson] = useState('{"provider":"openai","route":"openai/gpt-4o-mini","display_name":"GPT-4o mini","context_length":128000,"max_output":16384,"pricing":{"input":0.15,"output":0.6},"tier_min":"pro","priority":90,"enabled":true}');
  const [msg, setMsg] = useState<string | null>(null);

  const load = async () => {
    try {
      const { data } = await api<Array<Record<string, unknown>>>('/api/models?include_disabled=1');
      setModels(data);
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => { void load(); }, []);

  return (
    <div className="space-y-2">
      {msg && <p className="text-xs text-zinc-300">{msg}</p>}
      <button className="btn-ghost" onClick={() => void load()}>刷新列表</button>
      <table className="w-full text-xs">
        <tbody>
          {models.map((m) => (
            <tr key={String(m.id)} className="border-b border-zinc-800/60">
              <td className="py-1 font-mono">{String(m.id)}</td>
              <td>{String(m.display_name)}</td>
              <td>{String(m.tier_min)}</td>
              <td>{m.enabled ? 'on' : 'off'} · p{String(m.priority)}</td>
              <td>
                <button className="btn-ghost py-0" disabled={disabled} onClick={() => void (async () => {
                  await api(`/api/admin/models/${m.id}`, { method: 'PUT', body: { enabled: !m.enabled } });
                  await load();
                })()}>{m.enabled ? '停用' : '启用'}</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="grid gap-2 md:grid-cols-[160px_1fr_auto]">
        <input className="input font-mono text-xs" value={id} onChange={(e) => setId(e.target.value)} disabled={disabled} />
        <textarea className="input font-mono text-[11px]" rows={3} value={json} onChange={(e) => setJson(e.target.value)} disabled={disabled} />
        <button className="btn" disabled={disabled} onClick={() => void (async () => {
          setMsg(null);
          try {
            await api(`/api/admin/models/${id}`, { method: 'PUT', body: JSON.parse(json) });
            setMsg('已 upsert');
            await load();
          } catch (e) {
            setMsg(e instanceof Error ? e.message : String(e));
          }
        })}>upsert</button>
      </div>
    </div>
  );
}
